import { useSidebarContext } from '@nxsys/context/AppContextProvider/SidebarContextProvider';
import { useThemeContext } from '@nxsys/context/AppContextProvider/ThemeContextProvider';
import { useAuthMethod, useAuthUser } from '@nxsys/hooks/AuthHooks';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Dropdown } from 'antd';
import clsx from 'clsx';
import React from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { RiVipCrownFill } from 'react-icons/ri';
import {
  StyledCrUserInfo,
  StyledCrUserInfoAvatar,
  StyledCrUserInfoContent,
  StyledCrUserInfoInner,
  StyledUserArrow,
  StyledUsername,
  StyledUsernameInfo,
  StyledCrUserDesignation,
} from './index.styled';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { roles } from '@nxsys/store/constants';
import {
  ADMIN_LOGIN_AS,
  AUTH_CODE,
  AUTH_CODE_IS_BUREAU,
  PERMISSIONS,
  TOKEN,
  USER,
} from '@nxsys/store/constants/keys';

const roleDisplayNames: Record<string, string> = {
  [roles.supper_admin]: 'Super Admin',
  [roles.admin]: 'Admin',
  [roles.employer]: 'Employer',
  [roles.agency]: 'Agency',
  [roles.employee]: 'Employee',
};

type UserInfoProps = {
  hasColor?: boolean;
};
const UserInfo: React.FC<UserInfoProps> = ({ hasColor }) => {
  const { themeMode } = useThemeContext();
  const { logout } = useAuthMethod();
  const { user } = useAuthUser();
  const { sidebarColorSet } = useSidebarContext();
  const { allowSidebarBgImage } = useSidebarContext();
  const navigate = useNavigateWithQuery();
  const { messages } = useTypedIntl();

  const displayName = user?.forename
    ? `${user.forename} ${user.surname || ''}`.trim()
    : user?.email || '';
  const avatarUrl = user?.avatarUrl;
  const baseRoleName = roleDisplayNames[user?.role || ''] || 'User';
  const isLoginAs = user?.isSuperAdmin;

  const getUserAvatar = () => {
    if (displayName) {
      return displayName.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
  };

  const onLogout = async () => {
    sessionStorage.removeItem('AUTH_CODE_CLEARED');
    await logout();
    navigate('/signin');
  };

  const handleBackToSuperAdmin = () => {
    sessionStorage.removeItem('AUTH_CODE_CLEARED');
    // Restore super admin auth via localStorage and let the page reload re-init
    // the Redux store. Dispatching synchronously would re-render the current
    // route with role=super_admin and flash the 403 page before the reload.
    const adminUser = JSON.parse(localStorage.getItem(ADMIN_LOGIN_AS) || '{}');
    if (adminUser?.token) {
      localStorage.setItem(TOKEN, adminUser.token);
      localStorage.setItem(USER, JSON.stringify(adminUser.user));
      localStorage.setItem(PERMISSIONS, JSON.stringify([]));
      localStorage.setItem(AUTH_CODE, JSON.stringify(''));
      localStorage.removeItem(AUTH_CODE_IS_BUREAU);
      localStorage.removeItem(ADMIN_LOGIN_AS);
    }
    window.location.href = '/manage-account/admin';
  };

  const items = [
    {
      key: 'user-info',
      label: (
        <div
          style={{
            padding: '8px 4px 10px',
            borderBottom: isLoginAs ? 'none' : '1px solid #e8e8e8',
            marginBottom: isLoginAs ? 0 : 4,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: '#262626',
              marginBottom: 2,
            }}
          >
            {displayName}
          </div>
          {user?.email && (
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
              {user.email}
            </div>
          )}
          <span
            style={{
              display: 'inline-block',
              fontSize: 11,
              fontWeight: 500,
              padding: '2px 8px',
              borderRadius: 4,
              backgroundColor: '#e6f7ff',
              color: '#0958d9',
              border: '1px solid #91caff',
            }}
          >
            {baseRoleName}
          </span>
        </div>
      ),
      disabled: true,
    },
    ...(isLoginAs && user?.superAdminEmail
      ? [
          {
            key: 'impersonated-by',
            label: (
              <div
                style={{
                  borderBottom: '1px solid #e8e8e8',
                  background: '#fffbe6',
                  margin: '-4px -12px 4px',
                  padding: '8px 16px 10px',
                }}
              >
                <div
                  style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}
                >
                  Impersonated by
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#595959',
                    marginBottom: 4,
                  }}
                >
                  {user.superAdminEmail}
                </div>
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: 10,
                    fontWeight: 500,
                    padding: '1px 6px',
                    borderRadius: 4,
                    backgroundColor: '#f6ffed',
                    color: '#389e0d',
                    border: '1px solid #b7eb8f',
                  }}
                >
                  Super Admin
                </span>
              </div>
            ),
            disabled: true,
          },
        ]
      : []),
    ...(isLoginAs
      ? [
          {
            key: 'back-sa',
            label: (
              <div onClick={handleBackToSuperAdmin}>Manage other account</div>
            ),
          },
        ]
      : []),
    // Hidden while impersonating (Thang, 17/08). Both of these change what it
    // takes to get into the account being *borrowed*, not the admin's own: a
    // password reset or a rotated authenticator would lock the real owner out,
    // in their name and in their audit trail. `isLoginAs` is the only thing that
    // distinguishes the two sessions — a genuine super-admin login has
    // `isSuperAdmin === false` and still sees both items, which is correct,
    // because then the account really is theirs.
    ...(isLoginAs
      ? []
      : [
          {
            key: 'change-password',
            label: (
              // NX-4996. Was a dialog of its own. Both items now go to the same
              // account-security screen, so there is one place to change a
              // password rather than a dialog here and a page at a URL nothing
              // linked to.
              <div onClick={() => navigate('/account-security/password')}>
                {messages['common.changePassword']}
              </div>
            ),
          },
          {
            // NX-4996. Next to the password, because it is the same question:
            // what it takes to get into this account.
            key: 'two-factor',
            label: (
              <div onClick={() => navigate('/account-security/two-factor')}>
                Two-factor authentication
              </div>
            ),
          },
        ]),
    {
      key: 2,
      label: <div onClick={onLogout}>Logout</div>,
    },
  ];

  return (
    <>
      {hasColor ? (
        <StyledCrUserInfo
          style={{
            backgroundColor: allowSidebarBgImage
              ? ''
              : sidebarColorSet.sidebarHeaderColor,
            color: sidebarColorSet.sidebarTextColor,
          }}
          className={clsx('cr-user-info', {
            light: themeMode === 'light',
          })}
        >
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement="bottomRight"
            overlayStyle={{
              zIndex: 1052,
              minWidth: 150,
            }}
          >
            <StyledCrUserInfoInner className="ant-dropdown-link">
              <div style={{ position: 'relative' }}>
                {avatarUrl ? (
                  <StyledCrUserInfoAvatar src={avatarUrl} />
                ) : (
                  <StyledCrUserInfoAvatar>
                    {getUserAvatar()}
                  </StyledCrUserInfoAvatar>
                )}
                {isLoginAs && (
                  <div style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    border: '1.5px solid #faad14',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}>
                    <RiVipCrownFill size={12} color="#faad14" />
                  </div>
                )}
              </div>
              <StyledCrUserInfoContent className="cr-user-info-content">
                <StyledUsernameInfo>
                  <StyledUsername
                    className={clsx({
                      light: themeMode === 'light',
                    })}
                  >
                    {displayName || 'admin user '}
                  </StyledUsername>
                  <StyledUserArrow className="cr-user-arrow">
                    <FaChevronDown />
                  </StyledUserArrow>
                </StyledUsernameInfo>
                {isLoginAs && (
                  <StyledCrUserDesignation style={{ color: '#d46b08', fontSize: 11 }}>
                    Login as {baseRoleName}
                  </StyledCrUserDesignation>
                )}
              </StyledCrUserInfoContent>
            </StyledCrUserInfoInner>
          </Dropdown>
        </StyledCrUserInfo>
      ) : (
        <StyledCrUserInfo
          className={clsx('cr-user-info', {
            light: themeMode === 'light',
          })}
        >
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement="bottomRight"
            overlayStyle={{
              zIndex: 1052,
              minWidth: 150,
            }}
          >
            <StyledCrUserInfoInner className="ant-dropdown-link">
              <div style={{ position: 'relative' }}>
                {avatarUrl ? (
                  <StyledCrUserInfoAvatar src={avatarUrl} />
                ) : (
                  <StyledCrUserInfoAvatar>
                    {getUserAvatar()}
                  </StyledCrUserInfoAvatar>
                )}
                {isLoginAs && (
                  <div style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    border: '1.5px solid #faad14',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}>
                    <RiVipCrownFill size={12} color="#faad14" />
                  </div>
                )}
              </div>
              <StyledCrUserInfoContent className="cr-user-info-content">
                <StyledUsernameInfo>
                  <StyledUsername
                    className={clsx({
                      light: themeMode === 'light',
                    })}
                  >
                    {displayName || 'admin user '}
                  </StyledUsername>
                  <StyledUserArrow className="cr-user-arrow">
                    <FaChevronDown />
                  </StyledUserArrow>
                </StyledUsernameInfo>
                {isLoginAs && (
                  <StyledCrUserDesignation style={{ color: '#d46b08', fontSize: 11 }}>
                    Login as {baseRoleName}
                  </StyledCrUserDesignation>
                )}
              </StyledCrUserInfoContent>
            </StyledCrUserInfoInner>
          </Dropdown>
        </StyledCrUserInfo>
      )}
    </>
  );
};

export default UserInfo;
